/**
 * Value Prop module
 *
 * Image asset and copy in two columns.
 */

// Deps
import { createComponent, FelaComponent } from "react-fela";
import { combineRules } from "fela";
import { gutters, layout } from "~/styleguide";
import { textStyles } from "~/styleguide/typography";
import { fluid } from "~/styleguide/utils";
import { ContentMaxWidth } from "~/components/layout/max-width";
import dynamic from "next/dynamic";

// Context
import { getThemeForModule } from "~/context/theme";

// Components
import Visual from "~/components/shared/visual";
import { makeAutoplaySource, stripSecurityAttributes } from "~/modules/video";
import DeferredEmbed from "~/components/shared/deferred-embed";

const Animation = dynamic(() => import("~/components/shared/animation"), {
    ssr: false
});

export default function ValueProp({ block, isLcpCandidate }) {
    const {
        heading,
        subheading,
        copy,
        copy_html = "false",
        media_type = "image",
        image,
        video = "",
        video_embed,
        text_side,
        theme_colors,
        animation_file,
        anchor_id,
        preload
    } = block;

    // Determine color theme
    const theme = getThemeForModule(theme_colors);
    const shouldPreloadVisual =
        isLcpCandidate === true || (isLcpCandidate === undefined && preload);

    // Legacy shapes, removed from ACF, styles exist for only for desktop
    const show_filigree = false;
    const shape_style = null;

    return (
        <Container id={anchor_id} className="module-value-prop">
            <ContentMaxWidth extend={colWrapStyles({ textSide: text_side, videoStyle: video })}>
                {/* Content column */}
                <Col textSide={text_side} videoStyle={video}>
                    {/* Heading  */}

                    <FelaComponent style={{ maxWidth: 600 }}>
                        {heading && (
                            <FelaComponent
                                style={combineRules(
                                    valuePropHeadingStyle,
                                    textStyles.heading2
                                )}
                                as="h3"
                            >
                                <span dangerouslySetInnerHTML={{ __html: heading }} />
                            </FelaComponent>
                        )}

                        {subheading && (
                            <ValuePropText
                                as="p"
                                extend={textStyles.paragraphLarge}
                                subheading={true}
                                passThrough={["dangerouslySetInnerHTML"]}
                            >
                                <strong dangerouslySetInnerHTML={{ __html: subheading }} />
                            </ValuePropText>
                        )}

                        {/* Copy */}
                        {copy && copy_html === true &&(
                            <ValuePropText
                                as="div"
                                extend={textStyles.paragraphLarge}
                                dangerouslySetInnerHTML={{
                                    __html: copy
                                }}
                                passThrough={["dangerouslySetInnerHTML"]}
                            ></ValuePropText>
                        )}
                        {copy && copy_html === false &&(
                            <ValuePropText
                                as="p"
                                extend={textStyles.paragraphLarge}
                                dangerouslySetInnerHTML={{
                                    __html: copy
                                }}
                                passThrough={["dangerouslySetInnerHTML"]}
                            ></ValuePropText>
                        )}
                    </FelaComponent>
                </Col>

                {/* Media column */}
                <Col textSide={text_side} videoStyle={video}>
                    {/* Image and/or Video */}
                    {media_type === "image" && (
                        <Visual
                            deferVideoOnMobile
                            isLcpCandidate={isLcpCandidate}
                            load={shouldPreloadVisual}
                            autoplay
                            playsinline
                            muted
                            loop
                            video={video ? video : ""}
                            image={image}
                            extend={imageStyles}
                        />
                    )}

                    {/* Lottie Animation */}
                    {media_type === "animation" && animation_file && (
                        <Animation data={animation_file} />
                    )}

                    {/* Wistia Video */}
                    {media_type === "wistia" && (
                      <Content
                          extend={videoPlayerStyles}
                      >
                          <DeferredEmbed
                              className="video-player"
                              immediate={isLcpCandidate}
                              html={stripSecurityAttributes(
                                  makeAutoplaySource(video_embed)
                              )}
                          />
                      </Content>
                    )}
                    {/* Rounded border filigree */}
                    {show_filigree && (
                        <FelaComponent style={shapeWrapper}>
                            <DecorativeShape
                                shapeStyle={shape_style}
                                borderColor={theme.primary}
                                textSide={text_side}
                            ></DecorativeShape>
                        </FelaComponent>
                    )}
                </Col>
            </ContentMaxWidth>
        </Container>
    );
}

const videoPlayerStyles = {
    width: "100%"
};

const Content = createComponent(() => ({
    width: "50%",
    zIndex: 5,
    marginRight: 30,
    maxWidthMobileLandscape: {
        width: "100%",
        marginRight: 0,
        textAlign: "center"
    },
}));
//Margin in desktop comps is 140px, take 70px from each flex'd column, use space-between
const Container = createComponent(
    () => ({
        overflow: "hidden",
        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", gutters.vertical)
        ]
    }),
    "div",
    ["id"]
);

const imageStyles = {
    width: "100%",
    "& .vv-visual": {
        width: "100%",
        "& *": {
            width: "100%"
        }
    },
    "& .vv-video": {
        width: "100%",
        "& *": {
            width: "100%"
        }
    }
};

const colWrapStyles = ({ textSide }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    maxWidth: 1300,
    position: "relative",

    maxWidthMobile: {
        flexWrap: "wrap"
    },

    extend: [
        {
            condition: textSide === "right",
            style: {
                flexDirection: "row-reverse"
            }
        }
    ]
});

const colMargin = direction =>
    fluid(direction, {
        min: 20,
        max: 40,
        maxWidth: layout.contentMaxWidth
    });

const Col = createComponent(({ textSide, videoStyle }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    flexWrap: "wrap",

    ":first-of-type": {
        width: "55%"
    },
    ":last-of-type": {
        width: "45%"
    },

    minWidthMobile: {
    },

    maxWidthMobile: {
        width: "100%!important",
        textAlign: "center",
        ":first-of-type": {
            marginBottom: 44
        }
    },

    extend: [
        {
            condition: videoStyle,
            style: {
                ":first-of-type": {
                    width: "45%"
                },
                ":last-of-type": {
                    width: "55%"
                },
            }
        },
        {
            condition: textSide == "left",
            style: {
                minWidthTablet: {
                    ":first-of-type": {
                        extend: [colMargin("marginRight")]
                    }
                },

                minWidthMobile: {
                    ":last-of-type": {
                        extend: [colMargin("marginLeft")],
                        alignItems: "stretch"
                    }
                }
            }
        },
        {
            condition: textSide == "right",
            style: {
                minWidthTablet: {
                    ":first-of-type": {
                        extend: [colMargin("marginLeft")]
                    }
                },
                minWidthMobile: {
                    ":last-of-type": {
                        extend: [colMargin("marginRight")],
                        alignItems: "stretch"
                    }
                }
            }
        }
    ]
}));

const shapeWrapper = {
    width: "100%",
    position: "absolute",
    top: 0,
    bottom: 0,
    margin: "auto",
    maxWidthMobile: {
        display: "none"
    }
};

const DecorativeShape = createComponent(
    ({ shapeStyle, borderColor, textSide }) => ({
        border: "1px solid " + borderColor,
        position: "relative",
        overflow: "hidden",
        extend: [
            {
                condition: shapeStyle === "pill",
                style: {
                    width: "45vw",
                    maxWidth: 870,
                    height: "100%",
                    position: "absolute",
                    right: 0,
                    top: 0,

                    maxWidthMobile: {
                        maxWidth: "auto",
                        width: "110%"
                    },

                    extend: [
                        {
                            condition: textSide === "right",
                            style: {
                                borderRadius:
                                    "0px " +
                                    layout.siteMaxWidth / 4 +
                                    "px " +
                                    layout.siteMaxWidth / 4 +
                                    "px 0px"
                            }
                        },
                        {
                            condition: textSide === "left",
                            style: {
                                borderRadius:
                                    layout.siteMaxWidth / 4 +
                                    "px 0px 0px " +
                                    layout.siteMaxWidth / 4 +
                                    "px"
                            }
                        }
                    ]
                }
            },
            {
                condition: shapeStyle === "circle",
                style: {
                    borderRadius: "50%",
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    paddingTop: "100%",
                    left: 0,
                    top: 0
                }
            },
            {
                condition: textSide === "right",
                style: {
                    right: 0
                }
            },
            {
                condition: textSide === "left",
                style: {
                    left: 0
                }
            }
        ]
    })
);

const ValuePropText = createComponent(
    ({ subheading }) => ({
        extend: [
            {
                condition: subheading,
                style: {
                    marginBottom: 30
                }
            }
        ]
    }),
    "div",
    ["dangerouslySetInnerHTML"]
);

const valuePropHeadingStyle = {
    extend: [fluid("marginBottom", { min: 20, max: 50 })]
};
